import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faShoppingCart, faSpinner, faExclamationTriangle, faMinus, faPlus, faCheck } from '@fortawesome/free-solid-svg-icons';
import Footer from '../../Footer/Footer';
import BasketSidebar from '../../common/BasketSidebar';
import { useBasket } from '../../../contexts/BasketContext';
import { woocommerceFarmService } from '../../../services/woocommerceFarmService';

// Helper function to clean up HTML entities that may be double-encoded
const cleanHtmlContent = (html) => {
  if (!html) return '';
  // Replace &amp; with & to fix double-encoding issues
  return html.replace(/&amp;/g, '&');
};

const Container = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: ${props => props.theme.colors.offWhite};
  padding-top: 200px;
  overflow-x: hidden;
  max-width: 100vw;
  box-sizing: border-box;
  
  @media (max-width: 768px) {
    padding-top: 160px;
  }
`;

const Content = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 2rem;
  width: 100%;
  box-sizing: border-box;
  
  @media (max-width: 768px) {
    padding: 0 0.875rem;
  }
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: white;
  border: 2px solid ${props => props.theme.colors.primary};
  color: ${props => props.theme.colors.primary};
  padding: 0.8rem 1.5rem;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-bottom: 2rem;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 2rem;
  box-sizing: border-box;
  
  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
`;

const MainImage = styled.img`
  width: 100%;
  height: 500px;
  object-fit: cover;
  border-radius: 12px;
  
  @media (max-width: 768px) {
    height: 400px;
  }
`;

const Info = styled.div`
  padding: 2rem;
  background: white;
  border-radius: 16px;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.08);
  min-width: 0;
  box-sizing: border-box;
`;

const Title = styled.h1`
  font-family: ${props => props.theme.fonts.main};
  font-size: 2rem;
  color: ${props => props.theme.colors.primary};
  margin: 0 0 1rem 0;
  word-break: break-word;
  hyphens: auto;
`;

const Price = styled.div`
  font-size: 1.5rem;
  font-weight: 700;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 1rem;
`;

const Description = styled.div`
  font-size: 1rem;
  color: #333;
  line-height: 1.6;
  margin-bottom: 1.5rem;
  overflow-wrap: break-word;
  word-break: break-word;
  hyphens: auto;
  
  img {
    max-width: 100%;
    height: auto;
  }
`;

const Qty = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1rem;
`;

const QtyBtn = styled.button`
  width: 36px;
  height: 36px;
  border: 1px solid #e0e0e0;
  background: white;
  color: ${props => props.theme.colors.primary};
  border-radius: 8px;
  cursor: pointer;
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const QtyInput = styled.input`
  width: 50px;
  height: 36px;
  border: 1px solid #e0e0e0;
  text-align: center;
  border-radius: 8px;
`;

const AddBtn = styled.button`
  width: 100%;
  padding: 1rem 1.5rem;
  background: ${props => props.added ? '#4CAF50' : props.theme.colors.accent};
  color: white;
  border: none;
  border-radius: 10px;
  font-weight: 700;
  margin-top: 1rem;
  transition: background 0.2s ease, transform 0.2s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;

  &:hover {
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.85;
    cursor: not-allowed;
    transform: none;
  }
`;

const Center = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 1rem;
  padding: 4rem 2rem;
`;

const FarmShopProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToBasket, getItemQuantity } = useBasket();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [showBasket, setShowBasket] = useState(false);
  const [addStatus, setAddStatus] = useState(null);

  // Listen for global navbar event to open basket
  useEffect(() => {
    const handleOpenBasket = () => setShowBasket(true);
    document.addEventListener('openBasketSidebar', handleOpenBasket);
    return () => document.removeEventListener('openBasketSidebar', handleOpenBasket);
  }, []);

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        setLoading(true);
        setError(null);
        const p = await woocommerceFarmService.getProduct(id);
        setProduct(p);
      } catch (err) {
        setError('Failed to load product. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    if (id) fetchProduct();
  }, [id]);

  const stockStatus = product?.stock_status ?? 'instock';
  const manageStock = Boolean(product?.manage_stock);
  const stockQuantityRaw = manageStock ? parseInt(product?.stock_quantity ?? 0, 10) : null;
  const stockQuantity = manageStock && Number.isFinite(stockQuantityRaw) ? Math.max(stockQuantityRaw, 0) : null;
  const existingQty = product ? getItemQuantity(product.id) : 0;
  const remaining = manageStock && stockQuantity !== null ? Math.max(stockQuantity - existingQty, 0) : Infinity;
  const userLimitReached = manageStock && stockQuantity !== null && remaining <= 0;
  const isOutOfStock = stockStatus !== 'instock' || (manageStock && stockQuantity !== null && stockQuantity <= 0);

  const clampQuantity = (value) => {
    const numeric = Math.max(1, parseInt(value, 10) || 1);
    if (remaining === Infinity) return numeric;
    return Math.min(numeric, Math.max(1, remaining));
  };

  useEffect(() => {
    if (!product) return;
    if (isOutOfStock || userLimitReached) {
      setQuantity(0);
    } else {
      setQuantity(prev => {
        if (prev <= 0) return 1;
        if (remaining === Infinity) return prev;
        return Math.min(prev, Math.max(1, remaining));
      });
    }
  }, [product?.id, remaining, isOutOfStock, userLimitReached]);

  if (loading) {
    return (
      <Container>
        <Center>
          <FontAwesomeIcon icon={faSpinner} spin style={{ fontSize: '2rem', color: '#9d6d49' }} />
          <div>Loading product...</div>
        </Center>
        <Footer />
      </Container>
    );
  }

  if (error || !product) {
    return (
      <Container>
        <Center>
          <FontAwesomeIcon icon={faExclamationTriangle} style={{ fontSize: '2rem', color: '#f44336' }} />
          <div>{error || 'Product not found'}</div>
          <button onClick={() => navigate('/farm-shop')}>Back to Farm Shop</button>
        </Center>
        <Footer />
      </Container>
    );
  }

  const price = product.sale_price && product.sale_price !== product.regular_price ? product.sale_price : product.price;

  return (
    <Container>
      <Content>
        <BackButton onClick={() => navigate('/farm-shop')}>
          <FontAwesomeIcon icon={faArrowLeft} /> Back to Farm Shop
        </BackButton>

        <Grid>
          <div>
            <MainImage src={product.images?.[0]?.src || '/placeholder-product.jpg'} alt={product.name} onError={(e) => { e.target.src = '/placeholder-product.jpg'; }} />
          </div>
          <Info>
            <Title>{product.name.replace(/&amp;/g, '&')}</Title>
            <Price>£{price}</Price>
            <Description dangerouslySetInnerHTML={{ __html: cleanHtmlContent(product.description) }} />
            <Qty>
              <QtyBtn 
                onClick={() => setQuantity(prev => Math.max(1, prev - 1))}
                disabled={quantity <= 1 || quantity === 0}
              >
                <FontAwesomeIcon icon={faMinus} />
              </QtyBtn>
              <QtyInput
                type="number"
                value={quantity}
                onChange={(e) => setQuantity(clampQuantity(e.target.value))}
                min={1}
                max={remaining === Infinity ? undefined : remaining}
                disabled={isOutOfStock || userLimitReached}
              />
              <QtyBtn 
                onClick={() => setQuantity(prev => {
                  if (remaining === Infinity) return prev + 1;
                  return Math.min(prev + 1, Math.max(1, remaining));
                })}
                disabled={isOutOfStock || userLimitReached || (remaining !== Infinity && quantity >= remaining)}
              >
                <FontAwesomeIcon icon={faPlus} />
              </QtyBtn>
            </Qty>
            {isOutOfStock ? (
              <p style={{ color: '#c62828', fontWeight: 600, marginBottom: '0.5rem' }}>
                Currently sold out.
              </p>
            ) : userLimitReached ? (
              <p style={{ color: '#6b5b4f', fontSize: '0.9rem', marginBottom: '0.5rem' }}>
                You have added all available stock to your basket.
              </p>
            ) : manageStock && stockQuantity !== null ? (
              <p style={{ color: '#6b5b4f', fontSize: '0.9rem', marginBottom: '0.5rem' }}>
                {remaining === Infinity
                  ? 'In stock'
                  : remaining <= 5
                    ? `Only ${remaining} left in stock.`
                    : `In stock (${remaining} available)`}
              </p>
            ) : null}
            <AddBtn 
              added={addStatus === 'added'}
              disabled={isOutOfStock || userLimitReached || addStatus === 'limit' || addStatus === 'out' || quantity <= 0}
              onClick={() => {
                const result = addToBasket(product, quantity);
                if (!result?.success) {
                  const status = result?.reason === 'limit_reached' ? 'limit' : 'out';
                  setAddStatus(status);
                  setTimeout(() => setAddStatus(null), 2500);
                  return;
                }
                setAddStatus('added');
                setTimeout(() => setAddStatus(null), 2000);
              }}
            >
              {addStatus === 'added' ? (
                <>
                  <FontAwesomeIcon icon={faCheck} /> Added
                </>
              ) : (addStatus === 'limit' || userLimitReached) ? (
                <>
                  <FontAwesomeIcon icon={faExclamationTriangle} /> Max in basket
                </>
              ) : (addStatus === 'out' || isOutOfStock) ? (
                <>
                  <FontAwesomeIcon icon={faExclamationTriangle} /> Sold out
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faShoppingCart} /> Add to Basket
                </>
              )}
            </AddBtn>
          </Info>
        </Grid>
      </Content>
      <Footer />
      <BasketSidebar isOpen={showBasket} onClose={() => setShowBasket(false)} />
    </Container>
  );
};

export default FarmShopProductDetail;

